import type { Query, QueryClient } from "@tanstack/react-query";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/useUsers";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";

/**
 * The only queries whose data does not depend on the active client: who is
 * logged in, and the switcher's own client lists. Everything else is reset.
 *
 * The list is the fail-safe direction on purpose. Most query keys carry no
 * client id — `api.ts` names the client in the `X-Client-Id` header at request
 * time — so a key forgotten here is merely refetched, while a list of keys TO
 * reset would silently miss the next scoped one. That miss is how Loxago's
 * widget settings were saved with another tenant's values (2026-10-30).
 */
const CLIENT_INDEPENDENT_ROOTS: ReadonlySet<unknown> = new Set([
  CURRENT_USER_QUERY_KEY[0],
  "super-admin-clients",
  "my-clients",
]);

function isClientScoped(query: Query): boolean {
  return !CLIENT_INDEPENDENT_ROOTS.has(query.queryKey[0]);
}

/**
 * Drops every client-scoped query the moment the active client changes.
 *
 * Zustand runs the listener synchronously inside `setActiveClient`, before React
 * re-renders, so no component — remounted or still mounted — can read the
 * previous client's cache. A request already in flight carries the OLD header:
 * `Query.reset()` cancels it silently, so its late response is discarded
 * instead of landing in the new client's cache. (No `cancelQueries` first — its
 * default `revert` would put the old client's data back.) Reset rather than
 * remove, so observers that stay mounted across the switch (sidebar, top bar) go
 * back to loading and refetch instead of holding a query the cache no longer knows.
 */
export function bindQueryCacheToActiveClient(queryClient: QueryClient): () => void {
  return useClientSwitcherStore.subscribe((state, prev) => {
    if (state.activeClientId === prev.activeClientId) return;
    // A failed refetch already shows as that query's own error state.
    void queryClient.resetQueries({ predicate: isClientScoped }).catch(() => undefined);
  });
}
